// reveal-occurrence.ts — which chip on screen answers a sidebar row's
// "show this on the calendar".
//
// A chip carries `data-occurrence-key` = `occurrenceKey(event)`:
// `${'c'|'m'}:${_id}@${epochMs}`. A recurring message puts many chips with one
// `_id` on the grid, so the request carries the occurrence's due date too, and
// the chip with that id whose time is CLOSEST wins — an exact match when the
// sidebar and the grid agree to the millisecond, the right one anyway when they
// round differently. Pure, so it is tested without a DOM.

export interface RevealTarget {
    id: string;
    isCampaign: boolean;
    dueDate: string | Date;
}

/** How long a "show on calendar" request stays actionable. It answers a click,
 *  so it is acted on right then — including by a grid that mounts BECAUSE of it
 *  (the reveal switches the agenda back to the calendar) — and never later. */
export const REVEAL_REQUEST_TTL_MS = 3000;

/**
 * Should the grid act on this request? Once per request (`nonce`), and only
 * while it is fresh. The request lives in the store, so without the age check
 * every later mount of the grid — opening the planner again in the extension —
 * replayed the last click and flashed an item nobody had asked about.
 */
export function shouldActOnReveal(
    request: { nonce: number } | null | undefined,
    handledNonce: number | null,
    now: number,
): boolean {
    if (!request || request.nonce === handledNonce) return false;
    return now - request.nonce <= REVEAL_REQUEST_TTL_MS;
}

export const revealKeyPrefix = (target: RevealTarget): string =>
    `${target.isCampaign ? 'c' : 'm'}:${target.id}@`;

export function pickRevealKey(keys: readonly string[], target: RevealTarget): string | null {
    const prefix = revealKeyPrefix(target);
    const want = new Date(target.dueDate).getTime();
    let best: string | null = null;
    let bestDistance = Infinity;
    for (const key of keys) {
        if (!key.startsWith(prefix)) continue;
        const at = Number(key.slice(prefix.length));
        const distance = Number.isFinite(want) && Number.isFinite(at) ? Math.abs(at - want) : 0;
        if (distance < bestDistance) {
            best = key;
            bestDistance = distance;
        }
    }
    return best;
}
